/**
 * Webhook detail page - /settings/webhooks/:id.
 *
 * Layout matches the design mockup:
 *   - Header: back link, kind tag, status badge, name, full id (mono),
 *     plus right-aligned action buttons (Fire test / Rotate secret /
 *     Edit / Deactivate + a conditional Reactivate when the breaker tripped).
 *   - Tabs: Overview - Deliveries - Events - Settings
 *           (Deliveries fully wires in 3d; Events + Settings are stubs.)
 *   - Overview = two-col grid:
 *       Left:  Configuration panel + Recent deliveries (5 row preview).
 *       Right: Health rail + Created rail.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ArrowLeft, ArrowUpRight, Loader2, Play, RefreshCcw } from 'lucide-react'
import { toast } from 'sonner'

import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { useFormatting } from '@/admin/hooks/use-formatting'
import {
  useWebhookActions,
  usePulseWebhook,
  usePulseWebhookDeliveries,
  type PulseDeliveryRow,
} from '@/admin/hooks/use-pulse-webhooks'
import { toastApiError } from '@/shared/lib/api-error'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import type { PulseDerivedStatus } from '@/admin/types/pulse'

import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { useEventTypeLabel } from '@/admin/hooks/use-pulse-events'

import { EditWebhookDialog } from './edit-webhook-dialog'
import { EventMultiSelect } from './event-multi-select'
import { RevealWebhookSecretDialog } from './reveal-webhook-secret-dialog'
import { WebhookDeliveryDrawer } from './webhook-delivery-drawer'

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type Tab = 'overview' | 'deliveries' | 'events' | 'settings'

export function PulseWebhookDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const webhook = usePulseWebhook(id)
  const actions = useWebhookActions(id)
  const [tab, setTab] = useState<Tab>('overview')
  const [editOpen, setEditOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null)
  const [revealedPreviousExpiresAt, setRevealedPreviousExpiresAt] = useState<
    string | null
  >(null)
  const [drawerDelivery, setDrawerDelivery] = useState<string | null>(null)

  async function handleFireTest() {
    try {
      const result = await actions.fireTest.mutateAsync()
      toast.success(
        `Test event queued (delivery ${result.delivery_id.slice(0, 8)}...).`,
      )
    } catch (err) {
      toastApiError(err, 'Failed to fire test event.')
    }
  }

  async function handleRotateSecret() {
    try {
      const result = await actions.rotateSecret.mutateAsync()
      setRevealedSecret(result.secret)
      setRevealedPreviousExpiresAt(result.previous_secret_expires_at ?? null)
    } catch (err) {
      toastApiError(err, 'Failed to rotate secret.')
    }
  }

  async function handleDelete() {
    try {
      const result = await actions.remove.mutateAsync()
      toast.success(result.message)
      navigate('/settings/webhooks')
    } catch (err) {
      toastApiError(err, 'Failed to deactivate webhook.')
    }
  }

  async function handleReactivate() {
    try {
      await actions.reactivate.mutateAsync()
      toast.success('Webhook reactivated.')
    } catch (err) {
      toastApiError(err, 'Failed to reactivate webhook.')
    }
  }

  if (webhook.isLoading) {
    return (
      <div className="mt-8 flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--ink-3)]" />
      </div>
    )
  }

  if (webhook.error || !webhook.data) {
    return (
      <div className="mt-8">
        <BackLink onClick={() => navigate('/settings/webhooks')} />
        <h2 className="mt-3 text-[15px] font-medium text-[var(--ink)]">
          Webhook not found
        </h2>
        <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
          It may have been deleted, or you may not have access to it.
        </p>
      </div>
    )
  }

  const wh = webhook.data

  return (
    <div className="mt-8">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <BackLink onClick={() => navigate('/settings/webhooks')} />
          <div className="mt-3 flex items-center gap-2">
            <KindTag />
            <StatusBadge status={wh.status} />
          </div>
          <h1 className="mt-2 font-display text-[28px] leading-tight text-[var(--ink)]">
            {wh.description || 'Untitled webhook'}
          </h1>
          <p className="mt-1 truncate font-mono text-[11.5px] text-[var(--ink-3)]">
            sub_{wh.id}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {!wh.is_active && (
            <Button
              size="sm"
              onClick={handleReactivate}
              disabled={actions.reactivate.isPending}
            >
              {actions.reactivate.isPending ? (
                <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              ) : (
                <RefreshCcw className="mr-1.5 h-3 w-3" />
              )}
              Reactivate
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleFireTest}
            disabled={actions.fireTest.isPending || !wh.is_active}
            title={
              wh.is_active ? undefined : 'Reactivate the webhook to fire a test.'
            }
          >
            {actions.fireTest.isPending ? (
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            ) : (
              <Play className="mr-1.5 h-3 w-3" />
            )}
            Fire test event
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRotateSecret}
            disabled={actions.rotateSecret.isPending}
          >
            {actions.rotateSecret.isPending && (
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            )}
            Rotate secret
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteOpen(true)}
            className="text-[var(--red)] hover:bg-[var(--red-bg)]"
          >
            Deactivate
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-0 border-b-[0.5px] border-[var(--rule)]">
        <TabButton current={tab} value="overview" onClick={setTab}>
          Overview
        </TabButton>
        <TabButton current={tab} value="deliveries" onClick={setTab}>
          Deliveries
        </TabButton>
        <TabButton current={tab} value="events" onClick={setTab}>
          Events
        </TabButton>
        <TabButton current={tab} value="settings" onClick={setTab}>
          Settings
        </TabButton>
      </div>

      {/* Tab content */}
      <div className="mt-6">
        {tab === 'overview' && (
          <OverviewTab
            webhook={wh}
            onEdit={() => setEditOpen(true)}
            onOpenDelivery={setDrawerDelivery}
            onViewAllDeliveries={() => setTab('deliveries')}
          />
        )}
        {tab === 'deliveries' && <DeliveriesTab webhookId={wh.id} />}
        {tab === 'events' && (
          <EventsTab
            webhook={wh}
            isPending={actions.update.isPending}
            onSave={(enabled_events) =>
              // PUT is full-replacement on the backend - thread every
              // mutable field through with the current value so only
              // enabled_events actually changes.
              actions.update.mutateAsync({
                target_url: wh.target_url,
                description: wh.description,
                max_attempts: wh.max_attempts,
                is_active: wh.is_active,
                enabled_events,
              })
            }
          />
        )}
        {tab === 'settings' && (
          <PlaceholderTab
            title="Advanced settings"
            message="API version pinning + signing algorithm lands in a later release."
          />
        )}
      </div>

      {/* ── Dialogs ──────────────────────────────────────────────── */}
      <EditWebhookDialog
        open={editOpen}
        webhook={wh}
        isPending={actions.update.isPending}
        onClose={() => setEditOpen(false)}
        onSave={(changes) => actions.update.mutateAsync(changes)}
      />
      <ConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Deactivate this webhook?"
        description={
          <span>
            <code className="font-mono text-[11.5px]">{wh.target_url}</code>{' '}
            will stop receiving events. Its delivery history is preserved and
            you can reactivate it anytime.
          </span>
        }
        confirmLabel="Deactivate"
        pendingLabel="Deactivating..."
        destructive
        isPending={actions.remove.isPending}
        onConfirm={handleDelete}
      />
      <RevealWebhookSecretDialog
        open={revealedSecret !== null}
        secret={revealedSecret}
        description={wh.description}
        previousSecretExpiresAt={revealedPreviousExpiresAt}
        onClose={() => {
          setRevealedSecret(null)
          setRevealedPreviousExpiresAt(null)
        }}
      />
      <WebhookDeliveryDrawer
        open={drawerDelivery !== null}
        webhookId={wh.id}
        deliveryId={drawerDelivery}
        onClose={() => setDrawerDelivery(null)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Overview tab - config + recent deliveries on the left, health rail right
// ---------------------------------------------------------------------------

function OverviewTab({
  webhook,
  onEdit,
  onOpenDelivery,
  onViewAllDeliveries,
}: {
  webhook: import('@/admin/types/pulse').PulseWebhookSubscription
  onEdit: () => void
  onOpenDelivery: (id: string) => void
  onViewAllDeliveries: () => void
}) {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_280px]">
      <div className="space-y-5">
        <ConfigurationPanel webhook={webhook} onEdit={onEdit} />
        <RecentDeliveriesPanel
          webhookId={webhook.id}
          onOpenDelivery={onOpenDelivery}
          onViewAll={onViewAllDeliveries}
        />
      </div>
      <div className="space-y-4">
        <HealthRail />
        <CreatedRail webhook={webhook} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Deliveries tab - full paginated list with status filter + drawer.
// ---------------------------------------------------------------------------

type DeliveryFilter = 'all' | 'DELIVERED' | 'PENDING' | 'SUPPRESSED' | 'failed'

const DELIVERY_FILTER_LABELS: Record<DeliveryFilter, string> = {
  all: 'All',
  DELIVERED: 'Delivered',
  PENDING: 'Retrying',
  SUPPRESSED: 'Suppressed',
  failed: 'Failed',
}

function DeliveriesTab({ webhookId }: { webhookId: string }) {
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState<DeliveryFilter>('all')
  const [drawerDelivery, setDrawerDelivery] = useState<string | null>(null)
  const labelFor = useEventTypeLabel()
  const { locale } = useFormatting()

  // ``failed`` is a UI bucket covering every non-success terminal status -
  // EXHAUSTED / ENDPOINT_* / AUDIT_WRITE_FAILED / PAYLOAD_BUILD_FAILED.
  // The API filters by exact status string, so we approximate via the
  // most common ones and post-filter on the client. Fine while the list
  // page sizes are bounded; the real fix is a backend-side
  // ``status_group`` filter when the page gets used at scale.
  const apiParams: Record<string, string | number> = {
    page,
    page_size: 25,
  }
  if (filter !== 'all' && filter !== 'failed') apiParams.status = filter

  const deliveries = usePulseWebhookDeliveries(webhookId, apiParams)
  const rows = (deliveries.data?.results ?? []).filter((r) => {
    if (filter !== 'failed') return true
    return !['DELIVERED', 'PENDING', 'SUPPRESSED'].includes(r.status)
  })

  const columns: DataTableColumn<(typeof rows)[number]>[] = [
    {
      id: 'created',
      header: 'Time',
      cell: (row) => (
        <span className="font-mono text-[11px] text-[var(--ink-3)]">
          {new Date(row.created_at).toLocaleString(locale, {
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
          })}
        </span>
      ),
    },
    {
      id: 'event',
      header: 'Event',
      cell: (row) => (
        <span
          className="truncate text-[12.5px] text-[var(--ink)]"
          title={row.event_type}
        >
          {labelFor(row.event_type)}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => <InlineStatusBadge status={row.status} />,
    },
    {
      id: 'gen',
      header: 'Generation',
      cell: (row) =>
        row.attempt_generation > 0 ? (
          <Badge variant="outline" className="text-[var(--ink-3)]">
            Replay #{row.attempt_generation}
          </Badge>
        ) : (
          <span className="text-[11px] text-[var(--ink-3)]">Original</span>
        ),
    },
  ]

  return (
    <div className="space-y-4">
      {/* Status filter strip */}
      <div className="flex items-center gap-0 border-b-[0.5px] border-[var(--rule)]">
        {(Object.keys(DELIVERY_FILTER_LABELS) as DeliveryFilter[]).map((k) => (
          <Button
            key={k}
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilter(k)
              setPage(1)
            }}
            className={cn(
              '-mb-px h-auto rounded-none border-b-[1.5px] border-transparent px-3.5 py-2 text-[12px] font-normal transition-colors hover:bg-transparent',
              filter === k
                ? 'border-b-[var(--ink)] text-[var(--ink)]'
                : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
            )}
          >
            {DELIVERY_FILTER_LABELS[k]}
          </Button>
        ))}
      </div>

      {/* Table */}
      <DataTable
        tableId="webhook-deliveries"
        columns={columns}
        data={rows}
        isLoading={deliveries.isLoading}
        rowKey={(r) => r.id}
        onRowClick={(r) => setDrawerDelivery(r.id)}
        page={page}
        pageSize={25}
        totalCount={deliveries.data?.count ?? 0}
        onPageChange={setPage}
      />

      <WebhookDeliveryDrawer
        open={drawerDelivery !== null}
        webhookId={webhookId}
        deliveryId={drawerDelivery}
        onClose={() => setDrawerDelivery(null)}
      />
    </div>
  )
}

function InlineStatusBadge({ status }: { status: string }) {
  if (status === 'DELIVERED')
    return (
      <Badge
        variant="outline"
        className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      >
        Delivered
      </Badge>
    )
  if (status === 'PENDING')
    return (
      <Badge
        variant="outline"
        className="border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
      >
        Retrying
      </Badge>
    )
  if (status === 'SUPPRESSED')
    return (
      <Badge variant="outline" className="text-[var(--ink-3)]">
        Suppressed
      </Badge>
    )
  return <Badge variant="destructive">Failed</Badge>
}

// ---------------------------------------------------------------------------
// Events tab - add/remove subscribed events. Per-event filter editing
// lands with the visual clause builder in a follow-up phase; for now
// every event delivers unconditionally and we render the placeholder
// "No filter" copy per subscribed row so the seam is visible.
// ---------------------------------------------------------------------------

function EventsTab({
  webhook,
  isPending,
  onSave,
}: {
  webhook: import('@/admin/types/pulse').PulseWebhookSubscription
  isPending: boolean
  onSave: (enabled_events: string[]) => Promise<unknown>
}) {
  const labelFor = useEventTypeLabel()
  const [draft, setDraft] = useState<Set<string>>(
    () => new Set(webhook.enabled_events),
  )
  const original = useMemo(
    () => new Set(webhook.enabled_events),
    [webhook.enabled_events],
  )
  const dirty =
    draft.size !== original.size ||
    [...draft].some((k) => !original.has(k))

  function toggle(key: string) {
    setDraft((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function handleSave() {
    if (draft.size === 0) {
      toast.error('Subscribe to at least one event.')
      return
    }
    try {
      await onSave([...draft])
      toast.success('Subscribed events updated.')
    } catch (err) {
      toastApiError(err, 'Failed to update events.')
    }
  }

  return (
    <div className="space-y-5">
      {/* Currently subscribed - read-only summary */}
      <Panel>
        <PanelHead title={`Currently subscribed (${webhook.enabled_events.length})`} />
        {webhook.enabled_events.length === 0 ? (
          <p className="px-4 py-6 text-center text-[11.5px] text-[var(--ink-3)]">
            No events subscribed.
          </p>
        ) : (
          <div>
            {webhook.enabled_events.map((key) => (
              <div
                key={key}
                className="flex items-center justify-between gap-3 border-t-[0.5px] border-t-[var(--rule-light)] px-4 py-2.5 text-[12px] first:border-t-0"
              >
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] text-[var(--ink)]">
                    {labelFor(key)}
                  </div>
                  <div className="truncate font-mono text-[10.5px] text-[var(--ink-3)]">
                    {key}
                  </div>
                </div>
                <span
                  className="shrink-0 text-[11px] text-[var(--ink-3)]"
                  title="Per-event filters land in a follow-up release."
                >
                  No filter
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Edit subscription - full width so the picker can breathe */}
      <Panel>
        <PanelHead title="Edit subscription" />
        <div className="px-4 pb-4 pt-2">
          <p className="mb-3 text-[11.5px] text-[var(--ink-3)]">
            Tick or untick events to change what this webhook receives.
          </p>
          <EventMultiSelect selected={draft} onToggle={toggle} />
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDraft(new Set(webhook.enabled_events))}
              disabled={!dirty || isPending}
            >
              Reset
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!dirty || isPending}>
              {isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              Save
            </Button>
          </div>
        </div>
      </Panel>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Configuration panel
// ---------------------------------------------------------------------------

// Display labels for the platform auth-type enum (API contract values).
const AUTH_TYPE_LABELS: Record<string, string> = {
  none: 'None',
  bearer_static: 'Bearer token',
  basic: 'HTTP Basic',
  api_key_header: 'API key header',
  oauth2_client_credentials: 'OAuth2 client credentials',
}

function ConfigurationPanel({
  webhook,
  onEdit,
}: {
  webhook: import('@/admin/types/pulse').PulseWebhookSubscription
  onEdit: () => void
}) {
  return (
    <Panel>
      <PanelHead title="Configuration">
        <Button variant="ghost" size="xs" onClick={onEdit}>
          Edit
        </Button>
      </PanelHead>
      <div className="px-4 pb-4">
        <FieldRow label="Endpoint">
          <code className="break-all font-mono text-[11.5px] text-[var(--ink-2)]">
            {webhook.target_url}
          </code>
        </FieldRow>
        <FieldRow label="Max attempts">
          <span className="font-mono text-[12px]">{webhook.max_attempts}</span>
          <span className="ml-2 text-[11px] text-[var(--ink-3)]">
            - backoff 0s → 30s → 2m → 10m → 1h → 6h → 24h
          </span>
        </FieldRow>
        <FieldRow label="Signing">
          <Badge variant="outline" className="border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]">
            HMAC-SHA256
          </Badge>
          <span className="ml-2 text-[11px] text-[var(--ink-3)]">
            on <code className="font-mono">X-Pulse-Signature</code>
          </span>
        </FieldRow>
        <FieldRow label="Authentication">
          <Badge variant="outline" className="border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)]">
            {AUTH_TYPE_LABELS[webhook.auth?.type ?? 'none']}
          </Badge>
          {webhook.auth?.type === 'oauth2_client_credentials' && (
            <span className="ml-2 break-all text-[11px] text-[var(--ink-3)]">
              via <code className="font-mono">{webhook.auth.token_url}</code>
            </span>
          )}
          {webhook.auth?.type === 'api_key_header' && (
            <span className="ml-2 text-[11px] text-[var(--ink-3)]">
              header <code className="font-mono">{webhook.auth.header_name}</code>
            </span>
          )}
        </FieldRow>
        <FieldRow label="Filter">
          <span className="text-[11.5px] text-[var(--ink-3)]">
            No filter - every selected event fires.
          </span>
        </FieldRow>
        <FieldRow label="Subscribed events">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            {webhook.enabled_events.length === 0 ? (
              <span className="text-[11.5px] text-[var(--ink-3)]">
                No events subscribed.
              </span>
            ) : (
              webhook.enabled_events.map((evt, idx) => (
                <span key={evt} className="font-mono text-[11.5px] text-[var(--ink-2)]">
                  {evt}
                  {idx < webhook.enabled_events.length - 1 && (
                    <span className="px-1 text-[var(--ink-4)]">·</span>
                  )}
                </span>
              ))
            )}
          </div>
        </FieldRow>
      </div>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Recent deliveries panel
// ---------------------------------------------------------------------------

function RecentDeliveriesPanel({
  webhookId,
  onOpenDelivery,
  onViewAll,
}: {
  webhookId: string
  onOpenDelivery: (id: string) => void
  onViewAll: () => void
}) {
  const deliveries = usePulseWebhookDeliveries(webhookId, { page_size: 5 })

  return (
    <Panel>
      <PanelHead title="Recent deliveries">
        {deliveries.data && (
          <Button
            variant="ghost"
            size="xs"
            onClick={onViewAll}
            className="h-auto px-0 py-0 text-[11px] font-normal text-[var(--ink-2)] hover:bg-transparent hover:text-[var(--ink)]"
          >
            View all {deliveries.data.count.toLocaleString()} →
          </Button>
        )}
      </PanelHead>
      {deliveries.isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
        </div>
      ) : (deliveries.data?.results ?? []).length === 0 ? (
        <div className="px-4 py-6 text-center text-[11.5px] text-[var(--ink-3)]">
          No deliveries yet. They'll appear here once the first event fires.
        </div>
      ) : (
        <div>
          {deliveries.data!.results.map((d) => (
            <DeliveryRow
              key={d.id}
              delivery={d}
              onOpen={() => onOpenDelivery(d.id)}
            />
          ))}
        </div>
      )}
    </Panel>
  )
}

function DeliveryRow({
  delivery,
  onOpen,
}: {
  delivery: PulseDeliveryRow
  onOpen: () => void
}) {
  const { formatDate, locale } = useFormatting()
  const labelFor = useEventTypeLabel()
  const time = new Date(delivery.created_at).toLocaleTimeString(locale, {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
  return (
    <Button
      variant="ghost"
      onClick={onOpen}
      className="flex h-auto w-full items-center justify-start gap-3 rounded-none border-t-[0.5px] border-t-[var(--rule-light)] px-4 py-2.5 text-left text-[12px] font-normal transition-colors first:border-t-0 hover:bg-[var(--bg-subtle)]"
    >
      <span
        className="w-[68px] shrink-0 font-mono text-[11px] text-[var(--ink-3)]"
        title={formatDate(delivery.created_at)}
      >
        {time}
      </span>
      <span
        className="flex-1 truncate text-[12px] text-[var(--ink)]"
        title={delivery.event_type}
      >
        {labelFor(delivery.event_type)}
      </span>
      <DeliveryStatusBadge status={delivery.status} />
      <span className="ml-2 text-[11px] text-[var(--ink-3)]">→</span>
    </Button>
  )
}

function DeliveryStatusBadge({ status }: { status: string }) {
  // Tenant-friendly label + colour mapping for PulseDeliveryStatus.
  if (status === 'DELIVERED')
    return (
      <Badge
        variant="outline"
        className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      >
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--status-ok)]" />
        Delivered
      </Badge>
    )
  if (status === 'PENDING')
    return (
      <Badge
        variant="outline"
        className="border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
      >
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--gold-ink)]" />
        Retrying
      </Badge>
    )
  if (status === 'SUPPRESSED')
    return (
      <Badge variant="outline" className="text-[var(--ink-3)]">
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--ink-3)]" />
        Suppressed
      </Badge>
    )
  // Any terminal failure
  return (
    <Badge variant="destructive">
      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-current" />
      Failed
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Right rail - health + meta
// ---------------------------------------------------------------------------

function HealthRail() {
  // Per-webhook 7d health metrics aren't on the wire yet - the platform
  // /admin/pulse/stats/ endpoint is cross-kind. Adding a per-webhook
  // stats endpoint is a small follow-up; for now we render the rail
  // with structure-only placeholders so the layout matches the mockup.
  return (
    <Panel>
      <RailSection label="Health">
        <div className="font-display text-[22px] leading-none text-[var(--ink)]">
          -
        </div>
        <div className="mt-1 text-[11px] text-[var(--ink-3)]">last 7 days</div>
      </RailSection>
      <RailSection label="Failures (7d)">
        <div className="text-[12.5px]">- retried · - exhausted</div>
      </RailSection>
      <RailSection label="Consecutive failures">
        <div className="text-[12.5px]">
          0{' '}
          <span className="text-[11px] text-[var(--ink-3)]">
            (breaker trips at 5)
          </span>
        </div>
      </RailSection>
    </Panel>
  )
}

function CreatedRail({
  webhook,
}: {
  webhook: import('@/admin/types/pulse').PulseWebhookSubscription
}) {
  const { formatDate } = useFormatting()
  return (
    <Panel>
      <RailSection label="Created">
        <div className="text-[12px] text-[var(--ink)]">
          {formatDate(webhook.created_at)}
        </div>
      </RailSection>
    </Panel>
  )
}

// ---------------------------------------------------------------------------
// Reusable chrome
// ---------------------------------------------------------------------------

function BackLink({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="xs"
      onClick={onClick}
      className="h-auto gap-1 px-0 py-0 text-[11px] font-normal text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink-2)]"
    >
      <ArrowLeft className="h-3 w-3" />
      Back to Webhooks
    </Button>
  )
}

function KindTag() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-[4px] border-[0.5px] border-[var(--rule-light)] bg-[var(--bg-subtle)] px-2 py-0.5 text-[11px] font-normal text-[var(--ink-2)]">
      <ArrowUpRight className="h-3 w-3 opacity-70" />
      Webhook
    </span>
  )
}

function StatusBadge({ status }: { status: PulseDerivedStatus }) {
  if (status === 'healthy')
    return (
      <Badge
        variant="outline"
        className="border-[var(--status-ok-border)] bg-[var(--status-ok-bg)] text-[var(--status-ok)]"
      >
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--status-ok)]" />
        Active
      </Badge>
    )
  if (status === 'degraded')
    return (
      <Badge
        variant="outline"
        className="border-[var(--gold-border)] bg-[var(--gold-bg)] text-[var(--gold-ink)]"
      >
        <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--gold-ink)]" />
        Degraded
      </Badge>
    )
  return (
    <Badge variant="outline" className="text-[var(--ink-3)]">
      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--ink-3)]" />
      Inactive
    </Badge>
  )
}

function TabButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab
  value: Tab
  onClick: (next: Tab) => void
  children: React.ReactNode
}) {
  const active = current === value
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onClick(value)}
      className={cn(
        '-mb-px h-auto rounded-none border-b-[1.5px] border-transparent px-3.5 py-2 text-[12px] font-normal transition-colors hover:bg-transparent',
        active
          ? 'border-b-[var(--ink)] text-[var(--ink)]'
          : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
      )}
    >
      {children}
    </Button>
  )
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
      {children}
    </div>
  )
}

function PanelHead({
  title,
  children,
}: {
  title: string
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between border-b-[0.5px] border-[var(--rule)] px-4 py-3">
      <span className="text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {title}
      </span>
      {children}
    </div>
  )
}

function FieldRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-start gap-4 border-b-[0.5px] border-[var(--rule-light)] py-2.5 last:border-b-0">
      <div className="pt-0.5 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </div>
      <div className="text-[12.5px] text-[var(--ink)]">{children}</div>
    </div>
  )
}

function RailSection({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="border-b-[0.5px] border-[var(--rule-light)] px-4 py-3.5 last:border-b-0">
      <div className="mb-1.5 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </div>
      {children}
    </div>
  )
}

function PlaceholderTab({ title, message }: { title: string; message: string }) {
  return (
    <div className="rounded-[8px] border-[0.5px] border-dashed border-[var(--rule-strong)] bg-[var(--bg-subtle)] px-6 py-12 text-center">
      <h3 className="font-display text-[18px] text-[var(--ink)]">{title}</h3>
      <p className="mt-1 text-[12px] text-[var(--ink-3)]">{message}</p>
    </div>
  )
}
